import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Select as AntdSelect } from 'antd'
import type { SelectProps as AntdSelectProps, DefaultOptionType } from 'antd/es/select'
import { ChevronDown } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Input } from '@/components/lemmala-design-system/input/input'

import { MULTI_DROPDOWN_CLASS } from './consts'
import { CLEAR_ALL_LABEL, NO_VALUES_AVAILABLE_LABEL, SEARCH_PLACEHOLDER } from './labels'
import { SortableOption } from './sortable-option'

import ClearIcon from '@/assets/select-clear.svg'

export type InteractiveSelectProps<ValueType> = AntdSelectProps<ValueType> & {
  searchable?: boolean
  dropdownHeader?: ReactNode
  draggable?: boolean
  onReorder?: (orderedValues: Array<DefaultOptionType['value']>) => void
}

export type SelectOptionType = NonNullable<AntdSelectProps['options']>[number]

const EMPTY_CONTENT = (
  <div className='flex w-full items-center justify-center bg-main-background-card p-4 text-main-foreground-tertiary text-body-small'>
    {NO_VALUES_AVAILABLE_LABEL}
  </div>
)

const DEFAULT_SUFFIX_ICON = (
  <ChevronDown className='size-4 shrink-0 text-main-foreground-secondary' />
)

const DEFAULT_CLEAR_ICON = <ClearIcon aria-hidden='true' className='size-4 shrink-0' />

const DEFAULT_POPUP_CONTAINER = (trigger: HTMLElement) =>
  trigger.closest<HTMLElement>('[role="dialog"]') ?? document.body

type LabelRenderProps = Parameters<NonNullable<AntdSelectProps['labelRender']>>[0]

// rc-select only falls back to the raw `value` when `labelRender` returns nullish. Since we always
// return an element, reproduce that fallback ourselves — otherwise a `value` with no matching option
// (options still loading, or a consumer passing a display string) renders an empty trigger.
const DEFAULT_LABEL_RENDER = ({ label, value }: LabelRenderProps): ReactNode => {
  const content = label ?? value
  const title =
    typeof content === 'string' || typeof content === 'number' ? String(content) : undefined

  return (
    <span className='block min-w-0 truncate' title={title}>
      {content}
    </span>
  )
}

export const InteractiveSelect = <ValueType,>({
  className,
  popupClassName,
  suffixIcon,
  menuItemSelectedIcon,
  maxTagCount,
  mode,
  value,
  onChange,
  onFocus,
  onBlur,
  searchable,
  options,
  allowClear,
  dropdownHeader,
  dropdownRender,
  onDropdownVisibleChange,
  onClear,
  draggable,
  onReorder,
  labelRender,
  ...props
}: InteractiveSelectProps<ValueType>) => {
  // Expand all tags while focused so users can see and remove selections; collapse on blur.
  const [isFocused, setIsFocused] = useState(false)
  const [searchText, setSearchText] = useState('')

  // When draggable but no onReorder is provided, manage the order locally so the story / consumer
  // can still see drag take effect without wiring a callback.
  const manageLocalOrder = Boolean(draggable && !onReorder)
  const [localOrder, setLocalOrder] = useState<DefaultOptionType[] | null>(null)

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    if (manageLocalOrder) setLocalOrder((options ?? []) as DefaultOptionType[])
  }, [options, manageLocalOrder])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 2, tolerance: 5 } }),
  )

  const effectiveOptions = useMemo<DefaultOptionType[] | undefined>(() => {
    if (manageLocalOrder && localOrder) return localOrder
    return options as DefaultOptionType[] | undefined
  }, [manageLocalOrder, localOrder, options])

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchText) return effectiveOptions
    const needle = searchText.toLowerCase()
    return effectiveOptions?.filter((option) => {
      const { label, value: optionValue } = option
      if (typeof label === 'string') return label.toLowerCase().includes(needle)
      return typeof optionValue === 'string' ? optionValue.toLowerCase().includes(needle) : false
    })
  }, [searchable, searchText, effectiveOptions])

  const isMulti = mode === 'multiple' || mode === 'tags'
  const multiValue = isMulti && Array.isArray(value) ? (value as DefaultOptionType['value'][]) : []
  const showClearAll = multiValue.length > 0 && Boolean(allowClear)

  const isOptionSelected = (optionValue: DefaultOptionType['value']) =>
    isMulti ? multiValue.includes(optionValue) : value === optionValue

  const handleClearAll = () => {
    onChange?.([] as unknown as ValueType, [])
    onClear?.()
  }

  const handleSortableSelect = (optionValue: DefaultOptionType['value']) => {
    if (isMulti) {
      const next = multiValue.includes(optionValue)
        ? multiValue.filter((v) => v !== optionValue)
        : [...multiValue, optionValue]
      onChange?.(next as unknown as ValueType, [])
      return
    }
    const found = (effectiveOptions ?? []).find((o) => o.value === optionValue)
    onChange?.(optionValue as unknown as ValueType, (found ?? {}) as DefaultOptionType)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const source = effectiveOptions ?? []
    const oldIndex = source.findIndex((o) => String(o.value) === active.id)
    const newIndex = source.findIndex((o) => String(o.value) === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    const next = [...source]
    const [moved] = next.splice(oldIndex, 1)
    next.splice(newIndex, 0, moved)
    if (onReorder) onReorder(next.map((o) => o.value))
    if (manageLocalOrder) setLocalOrder(next)
  }

  const renderDraggableItems = (items: DefaultOptionType[]): ReactNode => {
    if (items.length === 0) return EMPTY_CONTENT
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((o) => String(o.value))}
          strategy={verticalListSortingStrategy}
        >
          <div className='max-h-64 overflow-y-auto'>
            {items.map((option) => (
              <SortableOption
                key={String(option.value)}
                option={option}
                isSelected={isOptionSelected(option.value)}
                onSelect={handleSortableSelect}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    )
  }

  const renderDropdown: AntdSelectProps['dropdownRender'] = (menu) => {
    let items: ReactNode = menu
    if (draggable && filteredOptions) items = renderDraggableItems(filteredOptions)
    else if (dropdownRender) items = dropdownRender(menu)

    return (
      <>
        {dropdownHeader && (
          <div className='px-2 py-1.5 text-main-foreground-primary text-label-small'>
            {dropdownHeader}
          </div>
        )}
        {searchable && (
          <div className='pb-1'>
            <Input
              type='search'
              placeholder={SEARCH_PLACEHOLDER}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              className='border-main-border-default'
            />
          </div>
        )}
        {items}
        {showClearAll && (
          <div className='flex w-full items-center px-2 pb-1 pt-2'>
            <div className='flex w-full items-start border-t border-main-border-default pt-1'>
              <button
                type='button'
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClearAll}
                className='text-main-foreground-accent text-label-small'
              >
                {CLEAR_ALL_LABEL}
              </button>
            </div>
          </div>
        )}
      </>
    )
  }

  const needsDropdownRender = Boolean(
    searchable || dropdownHeader || showClearAll || dropdownRender || draggable,
  )

  return (
    <AntdSelect
      {...props}
      className={cn('w-full', className)}
      mode={mode}
      value={value}
      allowClear={allowClear}
      onChange={onChange}
      onClear={onClear}
      options={filteredOptions}
      onFocus={(event) => {
        setIsFocused(true)
        onFocus?.(event)
      }}
      onBlur={(event) => {
        setIsFocused(false)
        onBlur?.(event)
      }}
      onDropdownVisibleChange={(open) => {
        if (!open) setSearchText('')
        onDropdownVisibleChange?.(open)
      }}
      maxTagCount={isFocused ? undefined : maxTagCount}
      labelRender={isMulti ? labelRender : (labelRender ?? DEFAULT_LABEL_RENDER)}
      suffixIcon={suffixIcon ?? DEFAULT_SUFFIX_ICON}
      clearIcon={props.clearIcon ?? DEFAULT_CLEAR_ICON}
      // Lemmala disables antd's default trailing checkmark; consumers can opt back in by passing an icon.
      menuItemSelectedIcon={menuItemSelectedIcon ?? null}
      getPopupContainer={props.getPopupContainer ?? DEFAULT_POPUP_CONTAINER}
      popupClassName={cn(isMulti && MULTI_DROPDOWN_CLASS, popupClassName)}
      notFoundContent={props.notFoundContent ?? EMPTY_CONTENT}
      dropdownRender={needsDropdownRender ? renderDropdown : undefined}
      // Multi-selects hide already-selected options via CSS (.lemmala-select-dropdown-multiple).
      // antd's virtual list still reserves rows for those hidden options, leaving phantom space at
      // the bottom and clipping later options until scrolled. Render without virtualization so the
      // popup measures only the visible options.
      virtual={props.virtual ?? !isMulti}
    />
  )
}
